import { Action } from 'redux';
import { List } from 'immutable';
import { ThunkDispatch } from '.';
import { ViewItem } from '../containers/soap-viewer/viewers/Viewer';

// Types

export type TraceLogState = {
	records: List<Record>,
	activeItem: ViewItem | null
	viewerOpen: boolean
	unread: number
}

export type Record = SoapExchange | SdkAction

export type SoapExchange = {
	class: 'soapexchange'
	id: number
	messageId: number,
	request: Message | null
	response: Message | null
	new: boolean
}

export type SdkAction = {
	class: 'sdkaction'
	method: string
	arguments: any
	result: string | null
	description: string | null
	user: string | null
	module: "Authentication" | "Data Collection" | null
	new: boolean
	platform: 'Android' | 'iOS'
}

export type Message = {
	class: 'message'
	id: number
	type: MessageType
	body: string
	time: number
	soapaction: string | null
	masked: boolean
}

export enum MessageType {
	soaprequest,
	soapresponse,
}

// Initialization

const initialState: TraceLogState = {
	records: List<Record>(),
	activeItem: null,
	viewerOpen: false,
	unread: 0,
}
// Action Definition

export const TRACELOG_NEW_MESSAGE = 'tracelog/NEW_MESSAGE'
export const TRACELOG_NEW_SDK_ACTION = 'tracelog/NEW_SDK_ACTION'
export const TRACELOG_CLEAR = 'tracelog/CLEAR'
export const TRACELOG_SET_ACTIVE_ITEM = 'tracelog/set_active_record'
export const TRACELOG_SET_VIEWER_VISIBILITY = 'tracelog/set_viewer_visibility'

interface TraceLogNewMessageAction extends Action {
	type: typeof TRACELOG_NEW_MESSAGE,
	message: Message
}

interface TraceLogNewSdkActionAction extends Action {
	type: typeof TRACELOG_NEW_SDK_ACTION,
	sdkaction: SdkAction
}

interface TraceLogClearAction extends Action {
	type: typeof TRACELOG_CLEAR,
}

interface TraceLogSetActiveItemAction extends Action {
	type: typeof TRACELOG_SET_ACTIVE_ITEM
	record: Record
	item: ViewItem | null
}

interface TraceLogSetViewerVisibility extends Action {
	type: typeof TRACELOG_SET_VIEWER_VISIBILITY,
	viewerOpen: boolean
}


// Reducer

export default (state: TraceLogState = initialState, action: Action) => {
	switch (action.type) {
		case TRACELOG_NEW_MESSAGE:
			let newMessageAction = action as TraceLogNewMessageAction
			let message = newMessageAction.message

			let index = state.records.findIndex(record => record.class === "soapexchange" && record.messageId === message.id)

			if (index >= 0) {
				return {
					...state,
					records: state.records.update(index, record => {
						let soapExchange = record as SoapExchange
						if (message.type === MessageType.soaprequest)
							soapExchange.request = message
						else if (message.type === MessageType.soapresponse)
							soapExchange.response = message
						soapExchange.new = true
						return soapExchange
					}),
					unread: _.get(state.records.get(index), 'new', false) ? state.unread : state.unread + 1
				}
			}
			else {
				let record = {
					class: 'soapexchange',
					id: state.records.size + 1,
					messageId: message.id,
					request: message.type === MessageType.soaprequest ? message : null,
					response: message.type === MessageType.soapresponse ? message : null,
					new: true
				} as SoapExchange

				return {
					...state,
					records: state.records.push(record),
					unread: state.viewerOpen ? state.unread : state.unread + 1
				} as TraceLogState
			}
		case TRACELOG_NEW_SDK_ACTION:
			let newSdkAction = action as TraceLogNewSdkActionAction
			let sdkaction = newSdkAction.sdkaction

			return {
				...state,
				records: state.records.push(sdkaction),
				unread: state.viewerOpen ? state.unread : state.unread + 1
			} as TraceLogState
		case TRACELOG_SET_ACTIVE_ITEM:
			let setActiveItemAction = action as TraceLogSetActiveItemAction
			return {
				...state,
				activeItem: setActiveItemAction.item,
				records: state.records.map(r => ({
					...r,
					new: setActiveItemAction.record === r ? false : r.new
				}))
			} as TraceLogState
		case TRACELOG_CLEAR:
			return {
				...state,
				records: List<Record>(),
				activeItem: null,
				unread: 0,
			} as TraceLogState
		case TRACELOG_SET_VIEWER_VISIBILITY:
			let viewerOpen = (action as TraceLogSetViewerVisibility).viewerOpen
			return {
				...state,
				viewerOpen,
				unread: 0,
				records: viewerOpen ? state.records : state.records.map(r => ({ ...r, new: false }))
			} as TraceLogState
		default:
			return state
	}
}

// Action Creators

export const newMessage = (message: Message) => ({
	type: TRACELOG_NEW_MESSAGE,
	message
} as TraceLogNewMessageAction)

export const newSdkAction = (sdkaction: SdkAction) => ({
	type: TRACELOG_NEW_SDK_ACTION,
	sdkaction
} as TraceLogNewSdkActionAction)

export const setActiveItem = (record: Record | null = null, item: ViewItem | null = null) => ({
	type: TRACELOG_SET_ACTIVE_ITEM,
	record,
	item
} as TraceLogSetActiveItemAction)

export const clearRecords = () => ({
	type: TRACELOG_CLEAR,
} as TraceLogClearAction)

export const handleNewBroadcast = (data: any) =>
	(dispatch: ThunkDispatch) => {
		let result = JSON.parse(data)

		if (_.has(MessageType, result.type)) {
			dispatch(newMessage({
				...result,
				class: 'message',
				type: MessageType[result.type]
			} as Message))
		} else if (result.type === "sdkaction") {
			dispatch(newSdkAction({
				...result,
				class: 'sdkaction',
				new: true
			}))
		}
	}

export const setViewerVisibility = (viewerOpen: boolean) => ({
	type: TRACELOG_SET_VIEWER_VISIBILITY,
	viewerOpen
}) as TraceLogSetViewerVisibility